import { useMemo, useState } from 'react';
import { PALETTE, minutesToHHMM } from '@micplot/shared';
import { usePresenters, useChannels, useAssignments, useCanEdit } from '../state/store';
import BoardActions from '../components/BoardActions';

/**
 * Gantt of channels against the clock. This is where a double-booking or a
 * changeover that is too tight becomes obvious at a glance.
 *
 * Only *scheduled* assignments are plotted. An assignment made without any
 * time information ("this mic is theirs for the whole show") has no position on
 * a timeline; drawing it as one used to stretch the axis across a full 24 hours
 * and render every bar edge to edge, which told you nothing.
 */
export default function Timeline() {
  const canEdit = useCanEdit();
  const presenters = usePresenters();
  const channels = useChannels();
  const assignments = useAssignments();

  const [pxPerMin, setPxPerMin] = useState(2);
  const byId = useMemo(() => new Map(presenters.map(p => [p.id, p])), [presenters]);

  const scheduled = assignments.filter(a => a.scheduled);
  const unscheduled = assignments.filter(a => !a.scheduled);

  /**
   * Frame the window around real show times. Presenter slots count even when
   * nobody is assigned yet, so the shape of the day is visible while planning.
   */
  const { from, to } = useMemo(() => {
    const starts = [
      ...scheduled.map(a => a.start),
      ...presenters.flatMap(p => p.slots.map(s => s.start)),
    ];
    const ends = [
      ...scheduled.map(a => a.end),
      ...presenters.flatMap(p => p.slots.map(s => s.end)),
    ];
    if (starts.length === 0) return { from: 8 * 60, to: 20 * 60 };
    return {
      from: Math.max(0, Math.floor((Math.min(...starts) - 60) / 60) * 60),
      to: Math.min(24 * 60, Math.ceil((Math.max(...ends) + 60) / 60) * 60),
    };
  }, [scheduled, presenters]);

  const span = Math.max(60, to - from);
  const width = span * pxPerMin;
  const hours = Math.ceil(span / 60);
  const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
  const nowVisible = nowMin >= from && nowMin <= to;

  const nothingToPlot = scheduled.length === 0
    && presenters.every(p => p.slots.length === 0);

  return (
    <div className="tl-wrap">
      <div className="tl-toolbar">
        <span className="panel-title">Timeline</span>
        <span className="count">{minutesToHHMM(from)}–{minutesToHHMM(to)}</span>
        <div className="header-spacer" />
        {/*
          * Scheduling belongs where the schedule is read. Auto-assign is a
          * time-based operation, so working it from the chart avoids bouncing
          * back to the board to run it and again to see the result.
          */}
        {canEdit && <BoardActions />}
        <span className="tl-sep" aria-hidden="true" />
        <button className="btn sm" onClick={() => setPxPerMin(v => Math.max(0.5, v / 1.5))}>
          Zoom out
        </button>
        <span className="count" style={{ width: 46, textAlign: 'center' }}>
          {pxPerMin.toFixed(1)}×
        </span>
        <button className="btn sm" onClick={() => setPxPerMin(v => Math.min(12, v * 1.5))}>
          Zoom in
        </button>
        <button className="btn sm" onClick={() => setPxPerMin(2)}>Reset</button>
      </div>

      {unscheduled.length > 0 && (
        <div className="banner" style={{ marginBottom: 10 }}>
          <strong>{unscheduled.length} assignment{unscheduled.length > 1 ? 's' : ''}</strong>
          <span>
            with no times — held for the whole show, so {unscheduled.length > 1 ? 'they are' : 'it is'}
            {' '}listed below the chart rather than plotted.
          </span>
        </div>
      )}

      {nothingToPlot ? (
        <div className="panel empty" style={{ minHeight: 220 }}>
          <div style={{ fontWeight: 600 }}>Nothing to plot yet.</div>
          <div style={{ fontSize: 12, maxWidth: 380 }}>
            The timeline shows when each presenter is on stage. Give a presenter
            time slots (Edit → Time slots), or import a run-of-show, and their
            assignments will appear here.
          </div>
        </div>
      ) : (
        <div className="tl-scroll">
          <div className="tl-grid" style={{ minWidth: width + 84 }}>
            <div className="tl-hours">
              <div className="tl-label" style={{ borderBottom: 0 }}>TX</div>
              {Array.from({ length: hours }, (_, i) => (
                <div key={i} className="tl-hour" style={{ width: 60 * pxPerMin }}>
                  {minutesToHHMM(from + i * 60)}
                </div>
              ))}
            </div>

            {channels.map(ch => (
              <div key={ch.id} className="tl-row">
                <div className="tl-label">
                  <span>TX {ch.number}</span>
                  <span className="chip">{ch.micType}</span>
                </div>
                <div className="tl-lane" style={{ width }}>
                  {nowVisible && (
                    <div className="tl-now" style={{ left: (nowMin - from) * pxPerMin }} />
                  )}
                  {scheduled
                    .filter(a => a.channelId === ch.id)
                    .map(a => {
                      const p = byId.get(a.presenterId);
                      if (!p) return null;
                      const left = (a.start - from) * pxPerMin;
                      const w = Math.max(14, (a.end - a.start) * pxPerMin);
                      return (
                        <div
                          key={a.id}
                          className="tl-bar"
                          style={{
                            left, width: w,
                            background: PALETTE[p.color % PALETTE.length],
                            opacity: a.deployed ? 1 : 0.6,
                          }}
                          title={`${p.name} · ${minutesToHHMM(a.start)}–${minutesToHHMM(a.end)}`
                            + (a.deployed ? '' : ' · staged')}
                        >
                          {p.name}
                        </div>
                      );
                    })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {unscheduled.length > 0 && (
        <div className="panel" style={{ marginTop: 10, flex: '0 0 auto' }}>
          <div className="panel-head">
            <span className="panel-title">Held for the whole show</span>
            <span className="count">{unscheduled.length}</span>
          </div>
          <div style={{ padding: 10, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {unscheduled.map(a => {
              const p = byId.get(a.presenterId);
              const ch = channels.find(c => c.id === a.channelId);
              if (!p) return null;
              return (
                <span key={a.id} className="whole-show"
                      style={{ background: PALETTE[p.color % PALETTE.length] }}>
                  <strong>TX {ch?.number ?? '?'}</strong> {p.name}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
